/**
 * View component for the shared player search results list.
 *
 * Renders player rows via FlashList for virtualized infinite scroll.
 * Shows loading spinner, error state, empty state, or paginated results.
 * Used by GlobalSearchModal, AddPlayerSearchSurface, and the shadow-team
 * add-player modal.
 * @module components/shared/search-modal/PlayerSearchResultsList/PlayerSearchResultsListView
 */

import React, { memo } from "react";

import NoPlayersFound from "@/components/organisms/shared/search-modal/NoPlayersFound";
import type { NoPlayersFoundActionPlacement } from "@/components/organisms/shared/search-modal/NoPlayersFound/NoPlayersFoundView";
import SearchError from "@/components/organisms/shared/search-modal/SearchError";
import { Box } from "@/components/atoms/Box";
import { FlashList, type ListRenderItem } from "@/components/atoms/FlashList";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { PlayerSearchPlayerFragment } from "@/generated/graphql";

/**
 * Props for PlayerSearchResultsListView
 */
interface PlayerSearchResultsListViewProps {
  /** Player results to display */
  readonly players: readonly PlayerSearchPlayerFragment[];
  /** Whether data is loading */
  readonly loading: boolean;
  /**
   * Whether a NEW query is in flight while the previous results are still
   * displayed. Renders a lightweight inline strip above the rows instead of
   * replacing them with the full-panel spinner (SE-7374).
   */
  readonly isRefreshing?: boolean;
  /** Whether results are empty */
  readonly isEmpty: boolean;
  /** Whether the search query returned an error */
  readonly isError: boolean;
  /** Whether a fetchMore request is currently in progress */
  readonly isLoadingMore: boolean;
  /** Pre-built render item callback from container (virtualized path) */
  readonly renderItem: ListRenderItem<PlayerSearchPlayerFragment>;
  /** Pre-built row renderer from container (non-virtualized path) */
  readonly renderRow: (player: PlayerSearchPlayerFragment) => React.ReactNode;
  /**
   * Whether this list may own a virtualized scroller. Defaults to true.
   * Hosts that are themselves a scroller pass false.
   */
  readonly isVirtualized?: boolean;
  /** End-reached handler for pagination (called by FlashList) */
  readonly onEndReached: () => void;
  /** Optional ManuallyAddPlayer option rendered in the empty state */
  readonly manuallyAddPlayerOption?: React.ReactNode;
  /** Optional ManuallyAddPlayer option rendered as a list footer when results exist */
  readonly manuallyAddPlayerFooterOption?: React.ReactNode;
  /** Optional subtitle override for the empty state copy */
  readonly emptySubtitle?: string;
  /**
   * Optional heading override for the empty state copy — used to surface a
   * distinct "we stopped searching" state instead of a confident "no players
   * found" when the empty result came from the auto-load budget running out
   * rather than a genuine zero-match search (SE-8324).
   */
  readonly emptyHeading?: string;
  /** Where the empty state's action sits relative to its message */
  readonly emptyActionPlacement?: NoPlayersFoundActionPlacement;
}

/** testID for the full-panel spinner shown when there is nothing to display yet */
export const RESULTS_LOADING_TEST_ID = "player-search-results:loading";

/** Loading spinner shown during initial load */
const loadingSpinner = (
  <Box
    className="flex-1 items-center justify-center py-12"
    testID={RESULTS_LOADING_TEST_ID}
  >
    <Spinner size="large" />
  </Box>
);

/** Footer spinner element shown while loading more pages */
const listFooterSpinner = (
  <Box className="items-center border-t border-outline-default py-4">
    <Spinner size="small" />
  </Box>
);

/** testID for the inline strip shown while refreshing over existing results */
export const RESULTS_REFRESHING_TEST_ID = "player-search-results:refreshing";

/**
 * Lightweight refresh indicator shown ABOVE existing rows while a newer
 * search term is in flight — the alternative to blanking the panel.
 */
const refreshingStrip = (
  <Box
    className="items-center border-b border-outline-subtle py-2"
    testID={RESULTS_REFRESHING_TEST_ID}
  >
    <Spinner size="small" />
  </Box>
);

/** Threshold ratio for FlashList onEndReached (0-1, fraction of visible length) */
const END_REACHED_THRESHOLD = 0.8;

/**
 * Renders the list footer with pagination spinner or manually-add-player option
 * @param isLoadingMore - Whether a fetchMore request is in progress
 * @param manuallyAddPlayerFooterOption - Optional footer content
 * @returns Footer element or null
 */
/**
 * Renders the list footer with pagination spinner or manually-add-player option
 * @param isLoadingMore - Whether a fetchMore request is in progress
 * @param manuallyAddPlayerFooterOption - Optional footer content
 * @returns Footer element or null
 */
const renderListFooter = (
  isLoadingMore: boolean,
  manuallyAddPlayerFooterOption?: React.ReactNode
): React.ReactElement | null => {
  if (!isLoadingMore && !manuallyAddPlayerFooterOption) return null;
  return (
    <>
      {isLoadingMore ? listFooterSpinner : null}
      {!isLoadingMore ? manuallyAddPlayerFooterOption : null}
    </>
  );
};

/** Which of the four mutually exclusive results states is showing. */
type ResultsState = "loading" | "error" | "empty" | "results";

/**
 * Resolves the single results state to render (SE-7374).
 *
 * Priority matters. The failure check sits ABOVE the empty check because
 * `isEmpty` is true for a failed query too (no rows, not loading, no next
 * page) — the previous ordering made `SearchError` unreachable and rendered
 * every failure as a confident "no players found".
 * @param args - State flags
 * @param args.loading - Whether a fetch is in flight
 * @param args.isError - Whether the active query failed
 * @param args.isEmpty - Whether the settled result set is empty
 * @param args.count - Number of rows currently available (may be stale)
 * @returns The state to render
 */
const resolveResultsState = ({
  loading,
  isError,
  isEmpty,
  count,
}: {
  readonly loading: boolean;
  readonly isError: boolean;
  readonly isEmpty: boolean;
  readonly count: number;
}): ResultsState => {
  if (count === 0 && loading) return "loading";
  if (count === 0 && isError) return "error";
  if (isEmpty) return "empty";
  return "results";
};

/**
 * View component for the shared player search results list.
 * @param props - Component props
 * @param props.players - Player results array
 * @param props.loading - Loading state
 * @param props.isRefreshing - Whether a newer query is in flight over existing results
 * @param props.isEmpty - Empty results state
 * @param props.isError - Whether the search query returned an error
 * @param props.isLoadingMore - Whether a fetchMore request is in progress
 * @param props.renderItem - Pre-built render callback
 * @param props.onEndReached - End-reached handler for pagination
 * @param props.manuallyAddPlayerOption - Optional ManuallyAddPlayer option for empty state
 * @param props.manuallyAddPlayerFooterOption - Optional ManuallyAddPlayer option for list footer
 * @param props.emptySubtitle - Optional subtitle override for the empty state copy
 * @param props.emptyHeading - Optional heading override for the empty state copy
 * @param props.emptyActionPlacement - Where the empty state's action sits
 * @param props.renderRow - Row renderer used on the non-virtualized path
 * @param props.isVirtualized - Whether this list may own a scroller
 */
/**
 * The virtualized list, wrapped in the refresh strip when a newer term is in
 * flight.
 *
 * The wrapper exists ONLY to host the strip. Settled results keep the list as
 * this component's direct output, exactly as before, so the hosts' flex layout
 * is untouched on the overwhelmingly common path.
 * @param props - View props.
 * @returns The list.
 */
const renderVirtualized = (
  props: PlayerSearchResultsListViewProps
): React.ReactElement => {
  const list = (
    <FlashList
      data={props.players as PlayerSearchPlayerFragment[]}
      renderItem={props.renderItem}
      onEndReached={props.onEndReached}
      onEndReachedThreshold={END_REACHED_THRESHOLD}
      ListFooterComponent={renderListFooter(
        props.isLoadingMore,
        props.manuallyAddPlayerFooterOption
      )}
      showsVerticalScrollIndicator={false}
    />
  );

  return props.isRefreshing === true ? (
    <Box className="flex-1">
      {refreshingStrip}
      {list}
    </Box>
  ) : (
    list
  );
};

/**
 * Plain rows, no virtualization and no end-reached pagination (SE-7242).
 *
 * This path is for a host that is ALREADY a scroller — `MobileSheetChrome`,
 * whose body is a ScrollView. A FlashList inside one has no measurable viewport
 * to virtualize against and fights the outer scroll on native; the shipped
 * `MobileAddPlayerSearchSurfaceView` maps its rows for exactly this reason.
 * Pagination goes with it: `onEndReached` cannot fire without a scroller of our
 * own, so the first page is what the host shows.
 * @param props - View props.
 * @returns The plain rows.
 */
const renderPlain = (
  props: PlayerSearchResultsListViewProps
): React.ReactElement => (
  <Stack space="0">
    {props.isRefreshing === true ? refreshingStrip : null}
    {props.players.map(player => (
      <React.Fragment key={player.id}>{props.renderRow(player)}</React.Fragment>
    ))}
    {props.manuallyAddPlayerFooterOption}
  </Stack>
);

/**
 * The no-results state.
 * @param props - View props.
 * @returns The empty state.
 */
const renderEmpty = (
  props: PlayerSearchResultsListViewProps
): React.ReactElement => (
  <NoPlayersFound
    manuallyAddPlayerOption={props.manuallyAddPlayerOption}
    subtitle={props.emptySubtitle}
    heading={props.emptyHeading}
    actionPlacement={props.emptyActionPlacement}
  />
);

/**
 * What to draw per resolved state.
 *
 * "results" splits on virtualization, which is a HOST capability rather than a
 * search state — a host that is already a scroller takes plain rows.
 */
const RENDER_BY_STATE: Readonly<
  Record<
    ResultsState,
    (props: PlayerSearchResultsListViewProps) => React.ReactElement
  >
> = {
  loading: () => loadingSpinner,
  error: () => <SearchError />,
  empty: renderEmpty,
  results: props =>
    props.isVirtualized === false
      ? renderPlain(props)
      : renderVirtualized(props),
};

/**
 * Renders the player search results.
 *
 * Results may be the PREVIOUS term's while a newer one is in flight — the
 * refresh strip says so and the panel does not blank.
 * @param props - View props.
 * @returns The results panel.
 */
const PlayerSearchResultsListView = (props: PlayerSearchResultsListViewProps) =>
  RENDER_BY_STATE[
    resolveResultsState({
      loading: props.loading,
      isError: props.isError,
      isEmpty: props.isEmpty,
      count: props.players.length,
    })
  ](props);

PlayerSearchResultsListView.displayName = "PlayerSearchResultsListView";

export default memo(PlayerSearchResultsListView);
